import { ItemModelV2 } from "../../../../../erp/inventory/models/item_model";
import { getItemsRepo } from "../../../../../erp/inventory/items/repository/items_repository";
import { OrderItemModelV2 } from "../../../../../service/models/orders/order_item_model";
import { ItemBulkValidateResult } from "./ItemBulkUpload";

export type ItemCategory = "product" | "service" | "raw";

export const CATEGORY_LABEL: Record<ItemCategory, string> = {
  product: "Products",
  service: "Services",
  raw: "Raw Materials",
};

export const CATEGORY_NOUN: Record<ItemCategory, string> = {
  product: "Finished Good",
  service: "Service",
  raw: "Raw Material",
};

export const classifyItem = (
  item?: ItemModelV2,
): ItemCategory | undefined => {
  if (!item) return undefined;
  if (item.type === "Service") return "service";
  if (item.type === "Product") {
    return item.subcategory === "Finished Goods" ? "product" : "raw";
  }
  return undefined;
};

export const TEMPLATE_CSV = "Item Code,Quantity,Price (excl. GST)";

// Same rule as the product rows (product_rowV2.tsx): the CSV price is the
// base price and the stored order price is GST-inclusive.
const getGstRate = (item?: ItemModelV2): number =>
  item?.tax_applicable ? (item?.tax_detail?.gst_rate ?? 0) : 0;

const round2 = (n: number): number =>
  Math.round((n + Number.EPSILON) * 100) / 100;

export const downloadSampleTemplate = () => {
  const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "order_items_template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const lookupItemByCode = async (
  code: string,
): Promise<ItemModelV2 | undefined> => {
  const res = await getItemsRepo({
    item_code: code,
    item_name: code,
    mpn: code,
    page: 1,
    count: 20,
    all: true,
  });
  const list: ItemModelV2[] = res?.data?.data ?? [];
  return list.find(
    (i) => (i.item_code ?? "").toLowerCase() === code.toLowerCase(),
  );
};

export const buildValidateRow =
  (section: ItemCategory, getExisting: () => OrderItemModelV2[]) =>
  async (
    code: string,
    qty: number,
    price: number,
  ): Promise<ItemBulkValidateResult> => {
    const item = await lookupItemByCode(code);
    if (!item) {
      return { success: false, error: `Item code ${code} not found.` };
    }
    const cat = classifyItem(item);
    if (cat !== section) {
      const label = cat ? CATEGORY_LABEL[cat] : "another";
      const noun = cat ? CATEGORY_NOUN[cat] : item.type;
      const detail =
        cat === "raw" ? ` (subcategory: ${item.subcategory})` : "";
      return {
        success: false,
        error: `${code} is a ${noun}${detail} — add it in the ${label} section.`,
      };
    }

    const existing = getExisting();
    if (
      existing.some(
        (it) => (it.item?.item_code ?? "").toLowerCase() === code.toLowerCase(),
      )
    ) {
      return { success: false, error: `${code} already added.` };
    }

    const data: OrderItemModelV2 = {
      item_id: item.id,
      item,
      price: round2((price ?? 0) * (1 + getGstRate(item) / 100)),
      qty,
      delivered_qty: 0,
      installed_qty: 0,
      returned_qty: 0,
      created_at: Date.now(),
    };
    return { success: true, data, name: item.name };
  };
